// Content for the /for/:segment landing pages. One entry per audience.
// Each page shares the same template (SegmentPage.js) but has its own copy,
// pain points and a single worked "Example" calculation.
export const segments = {
  bakers: {
    title: 'Recipe Costing for Bakers | CostCraft',
    metaDescription:
      'CostCraft helps home and small-batch bakers cost every recipe down to the cent, track margins and price with confidence. Free Starter plan, no credit card.',
    headline: 'Recipe costing built for bakers',
    subheadline:
      "Know the true cost of every cake, batch and box before you price it — not after the flour and butter bill arrives.",
    painPoints: [
      'Butter, chocolate and flour prices jump overnight and your spreadsheet never catches up.',
      'Hours of mixing, baking and decorating time quietly disappear into a vague "overheads" number.',
      'You\'re guessing at wholesale prices for cafes and stockists instead of knowing your real margin.',
    ],
    example: {
      label: 'Example',
      title: 'Batch of 12 cupcakes',
      lines: [
        { name: 'Ingredients (flour, sugar, butter, eggs, frosting)', value: '$8.40' },
        { name: 'Labour (25 min mixing & baking @ $28/hr)', value: '$11.67' },
        { name: 'Overhead (power, packaging — 12% of cost)', value: '$2.41' },
        { name: 'Total batch cost', value: '$22.48', bold: true },
        { name: 'Cost per cupcake', value: '$1.87', bold: true },
      ],
    },
  },
  'market-stalls': {
    title: 'Pricing for Market Stall Sellers | CostCraft',
    metaDescription:
      'CostCraft helps market stall sellers cost jars, bags and baked goods including packaging and stall fees, so every price actually covers your costs.',
    headline: 'Pricing that works for market stalls',
    subheadline:
      'Jars, labels, stall fees and ingredients all add up. CostCraft rolls them into one true cost per unit, so you know which products actually pay.',
    painPoints: [
      'Jar, label and ingredient costs blur together and nobody is sure what a jar really costs to make.',
      'Stall fees and travel eat into margin without ever being tracked against a product.',
      "It's hard to know which flavour or product line is actually worth making again next week.",
    ],
    example: {
      label: 'Example',
      title: 'Jar of strawberry jam',
      lines: [
        { name: 'Fruit, sugar & pectin', value: '$1.85' },
        { name: 'Jar, lid & label', value: '$0.95' },
        { name: 'Labour (cooking & bottling, pro-rated)', value: '$1.10' },
        { name: 'Stall fee overhead (allocated per jar)', value: '$0.60' },
        { name: 'Total cost per jar', value: '$4.50', bold: true },
      ],
    },
  },
  'food-trucks': {
    title: 'Menu Costing for Food Trucks | CostCraft',
    metaDescription:
      'CostCraft helps food truck owners cost every menu item including bun, patty, sauce and packaging, so every serve is priced to actually turn a profit.',
    headline: 'Menu costing built for food trucks',
    subheadline:
      'Bun, patty, sauce, packaging and prep time all hit your margin. See the true cost of every serve before you set a menu price.',
    painPoints: [
      'Bun, patty, sauce and packaging costs shift week to week and the menu price never keeps up.',
      'Portion sizes drift on a busy service and nobody notices the margin quietly leaking away.',
      'Menu pricing feels like a guess instead of a number you can actually stand behind.',
    ],
    example: {
      label: 'Example',
      title: 'Classic beef burger, per serve',
      lines: [
        { name: 'Bun, patty, cheese & salad', value: '$3.60' },
        { name: 'Sauce & condiments', value: '$0.35' },
        { name: 'Packaging (box, napkin)', value: '$0.45' },
        { name: 'Labour (grill & assembly, pro-rated)', value: '$1.80' },
        { name: 'Total cost per serve', value: '$6.20', bold: true },
      ],
    },
  },
  caterers: {
    title: 'Platter & Event Costing for Caterers | CostCraft',
    metaDescription:
      'CostCraft helps caterers cost platters and events per head, including ingredients, prep labour and overheads, so every quote protects your margin.',
    headline: 'Platter and event pricing for caterers',
    subheadline:
      "Stop pricing platters by gut feel. See the real cost per head — ingredients, prep labour and overheads — before you send the quote.",
    painPoints: [
      'Platters get priced by gut feel, not by what they actually cost to prepare.',
      'Labour for prep, cooking and service rarely gets costed into the final quote.',
      'Every event has a different head count, and recalculating cost per head by hand is slow and error-prone.',
    ],
    example: {
      label: 'Example',
      title: 'Grazing platter, serves 10',
      lines: [
        { name: 'Ingredients (cheese, meats, fruit, crackers)', value: '$42.00' },
        { name: 'Labour (prep & styling, 40 min @ $30/hr)', value: '$20.00' },
        { name: 'Overhead (packaging, travel — 10% of cost)', value: '$6.20' },
        { name: 'Total platter cost', value: '$68.20', bold: true },
        { name: 'Cost per head', value: '$6.82', bold: true },
      ],
    },
  },
};

export const segmentOrder = ['bakers', 'market-stalls', 'food-trucks', 'caterers'];

export const segmentLabels = {
  bakers: 'Bakers',
  'market-stalls': 'Market Stall Sellers',
  'food-trucks': 'Food Trucks',
  caterers: 'Caterers',
};
